import { useState, type FormEvent } from 'react';
import { Circle, CircleCheckBig } from 'lucide-react';
import DialogError from '@/shared/ui/DialogError';
import { primaryButtonClass } from '@/shared/ui/formClasses';
import Modal, { ModalBody, ModalFooter, ModalForm } from '@/shared/ui/Modal';
import { checkFirstLoginPassword } from '../model/passwords';
import PasswordField from './PasswordField';

type FirstLoginDialogProps = {
  submitting: boolean;
  error?: string;
  onSubmit: (password: string) => void;
};

// Un requisito non ancora rispettato è neutro: chi scrive non ha sbagliato niente.
function Requirement({ met, children }: { met: boolean; children: string }) {
  const Icon = met ? CircleCheckBig : Circle;
  return (
    <span
      className={`flex items-center gap-2 text-caption ${met ? 'text-success' : 'text-subtle'}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {children}
      <span className="sr-only">{met ? ': soddisfatto' : ': non soddisfatto'}</span>
    </span>
  );
}

/** Il cambio password obbligatorio dopo il primo login: non si chiude finché la password non cambia. */
export default function FirstLoginDialog({ submitting, error, onSubmit }: FirstLoginDialogProps) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { rules, valid } = checkFirstLoginPassword(password);
  const matches = confirmation.length > 0 && password === confirmation;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit(password);
  };

  return (
    <Modal
      title="Primo accesso"
      description="Per motivi di sicurezza, devi cambiare la password al primo accesso."
    >
      <ModalForm noValidate onSubmit={handleSubmit}>
        <ModalBody>
          {error && <DialogError>{error}</DialogError>}
          <PasswordField
            id="first-login-password"
            label="Nuova password"
            required
            autoFocus
            autoComplete="new-password"
            placeholder="Inserisci la nuova password"
            aria-describedby="first-login-rules"
            value={password}
            onChange={setPassword}
            visible={showPassword}
            onToggleVisible={() => setShowPassword((visible) => !visible)}
          />
          <ul id="first-login-rules" aria-label="Requisiti password" className="space-y-1">
            {rules.map((rule) => (
              <li key={rule.key}>
                <Requirement met={rule.met}>{rule.label}</Requirement>
              </li>
            ))}
          </ul>
          <PasswordField
            id="first-login-confirmation"
            label="Conferma password"
            required
            autoComplete="new-password"
            placeholder="Conferma la nuova password"
            aria-describedby={confirmation ? 'first-login-match' : undefined}
            value={confirmation}
            onChange={setConfirmation}
            visible={showConfirmation}
            onToggleVisible={() => setShowConfirmation((visible) => !visible)}
          />
          <p id="first-login-match" aria-live="polite">
            {confirmation && (
              <Requirement met={matches}>
                {matches ? 'Le password coincidono' : 'Le password non coincidono'}
              </Requirement>
            )}
          </p>
        </ModalBody>
        <ModalFooter>
          <button
            type="submit"
            disabled={!valid || !matches || submitting}
            className={primaryButtonClass}
          >
            {submitting ? 'Aggiornamento…' : 'Cambia password'}
          </button>
        </ModalFooter>
      </ModalForm>
    </Modal>
  );
}
